import React, { useState, useEffect, useContext } from 'react';
import { useTransition, useChain, animated, useSpringRef } from '@react-spring/web';
import { getSurgeonPatientUsers } from '../../../../api/users';

import NewPatientMenu from '../new_patient_menu';
import styles from './index.module.css';
import Context from '../../../../utils/context';
import Skeleton from '../../../ui_kit/utilities/skeleton';
import StatusMessage from '../../../ui_kit/utilities/status_message';
import TableWithPagination from '../../../ui_kit/tables';

export default function MentorConnect() {
  const [view, setView] = useState('loading');
  const [searchTerm, setSearchTerm] = useState();
  const [users, setUsers] = useState([]);

  const context = useContext(Context);
  const currentUser = context.auth.getCurrentUser();
  const isMobile = context.viewport.getIsMobile();
  const headers = [
    'Patient name',
    'Sign up date',
    ...(currentUser.is_reactiv8 || currentUser.is_ossio || currentUser.is_medtronic ? ['Zipcode'] : []),
    'Initiated conversations',
    'Completed conversations',
    'Ready for next step',
  ];

  const keys = [
    'name',
    'created_at',
    ...(currentUser.is_reactiv8 || currentUser.is_ossio || currentUser.is_medtronic ? ['zipcode'] : []),
    'initiated_conversations_count',
    'completed_conversations_count',
    'ready_for_next_step',
  ];

  const tableSkeletonAnimation = useSpringRef();
  const tableSkeleton = useTransition(view === 'loading' ? Array(10).fill({}) : users, {
    ref: tableSkeletonAnimation,
    trail: 500 / 15,
    config: { mass: 0.5, tension: 200, friction: 20 },
    from: { opacity: 0, transform: 'translateX(50px)' },
    enter: { opacity: 1, transform: 'translateX(0px)', position: 'relative' },
  });

  useChain([tableSkeletonAnimation]);

  const getStatusColor = (value) => {
    if (value === 0) return '#EC4E3E'; // red for 0
    return '#24A845'; // green for 1 or above
  };

  const onFilterClick = () => {
    Promise.all([
      setView('loading'),

      new Promise((resolve) => {
        getSurgeonPatientUsers(
          { search_term: searchTerm, surgeon_id: currentUser.surgeon_administrative_assistants_surgeons[0]?.id },
          (usersArr) => {
            setUsers(usersArr);
            resolve();
          }
        );
      }),
    ]).finally(() => {
      setView('loaded');
    });
  };

  const getReadyForNextStepStatus = (readyForNextStep) => {
    if (readyForNextStep === 'yes' || readyForNextStep === 'maybe') {
      return <StatusMessage status='success' message='Yes' isMobile={isMobile} />;
    }

    if (readyForNextStep === 'no') {
      return <StatusMessage status='error' message='No' isMobile={isMobile} />;
    }

    return <StatusMessage status='infoBlue' message='Did not take survey' isMobile={isMobile} />;
  };

  const getReadyForNextStepSortValue = (readyForNextStep) => {
    const statusOrder = {
      yes: 1,
      maybe: 1,
      no: 3,
      no_answer: 4,
    };
    return statusOrder[readyForNextStep] || 5;
  };

  useEffect(() => {
    Promise.all([
      new Promise((resolve) => {
        getSurgeonPatientUsers(
          { surgeon_id: currentUser.surgeon_administrative_assistants_surgeons[0]?.id },
          (usersArr) => {
            setUsers(usersArr);
            resolve();
          }
        );
      }),
    ]).finally(() => {
      setView('loaded');
    });
  }, []);

  return (
    <div>
      {view === 'loading' && (
        <div className={`${styles.roster_container_wrapper}`}>
          <Skeleton />
          <div className={`${styles.roster_container} container-fluid`}>
            {tableSkeleton((style, item) => (
              <animated.div key={item} style={{ ...style }}>
                <div className={`${styles.roster_row}`}>
                  <div className={`row h-100 ${styles.roster_row_hover}`}>
                    <div className={`col cursor-pointer ${styles.roster_col_skeleton}`}>
                      <svg width='11' height='10' viewBox='0 0 11 10' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path
                          d='M5.56152 10C8.32295 10 10.5615 7.76142 10.5615 5C10.5615 2.23858 8.32295 0 5.56152 0C2.8001 0 0.561523 2.23858 0.561523 5C0.561523 7.76142 2.8001 10 5.56152 10Z'
                          fill='#eee'
                        />
                      </svg>
                    </div>
                    <div className={`col cursor-pointer ${styles.roster_col_skeleton}`}>
                      <Skeleton />
                    </div>
                    <div className={`col cursor-pointer ${styles.roster_col_skeleton}`}>
                      <Skeleton />
                    </div>
                    <div className={`col cursor-pointer ${styles.roster_col_skeleton}`}>
                      <Skeleton />
                    </div>
                    <div className={`col cursor-pointer ${styles.roster_col_skeleton}`}>
                      <Skeleton />
                    </div>
                    <div className={`col my-auto`}>
                      <Skeleton shape='box' />
                    </div>
                  </div>
                </div>
              </animated.div>
            ))}
          </div>
        </div>
      )}
      {view === 'loaded' && (
        <div className={`${styles.roster_container_wrapper}`}>
          <div className={styles.mentor_connect_heading} style={{ marginTop: '20px', marginBottom: '35px' }}>
            <h5 className={`${styles.roster_heading}`}>MentorConnect</h5>
            <NewPatientMenu />
          </div>
          <div className={`material-input ${styles.filter_input_container}`}>
            <input type='text' value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            <button className={`submit ${styles.filter_input_submit}`} onClick={onFilterClick}>
              Search
            </button>
          </div>
          <div className={`${styles.roster_container}`}>
            {users.length === 0 ? (
              <div className={styles.no_patients_notif_wrap}>
                <div className={styles.no_patients_notif}>
                  <img
                    src='https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/icons/crying-emoji.jpg'
                    alt='sad face'
                  />
                  <h5>No patients invited!</h5>
                  <p>
                    It doesn't look like any patients have been invited to speak with a mentor. Click
                    <span>+ Invite new patient</span> to invite a new patient.
                  </p>
                </div>
              </div>
            ) : (
              <TableWithPagination
                headers={headers}
                objects={users}
                keys={keys}
                isMobile={isMobile}
                formatting={{
                  created_at: (value) => new Date(value).toLocaleDateString('en'),
                  ready_for_next_step: (value) => ({
                    display: getReadyForNextStepStatus(value),
                    sortValue: getReadyForNextStepSortValue(value),
                  }),
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
